import { Trans } from 'react-i18next';

import { StructuredAnswer } from '@dreryk/egabinet-survey-frontend-api';

import { commonTranslationKeys } from '@dreryk/react-common';
import { ModalConfirmExitConfig } from '@dreryk/react-components';

import { appointmentsTranslationKeys } from '../../translations';

import { SectionSavePayload } from './MedicalProfileModal.types';
import { applyShowOnVisitToPayload } from './seam/showOnVisitSeam';
import { answerToFormValue, areFormValuesEqual, formValueToAnswer } from './tab/MedicalProfileAnswer.utils';
import {
   MedicalProfileAnswerValue,
   MedicalProfileTabFormValues,
   MedicalProfileTabViewModel,
   SectionNodeViewModel,
   SectionRowViewModel,
   VisibilityResolver,
} from './tab/MedicalProfileTab.types';
import {
   answerToCodes,
   createFormFieldName,
   createShowOnVisitFieldName,
   valueToCodes,
} from './tab/MedicalProfileTab.utils';
import { createVisibilityResolver, GATE_YES_CODE } from './tab/MedicalProfileVisibility.utils';

export const medicalProfileModalConfirmExitConfig = (isDirty: boolean): ModalConfirmExitConfig => ({
   enabled: isDirty,
   cancelButtonLabel: commonTranslationKeys.cancel,
   confirmationButtonLabel: appointmentsTranslationKeys.medicalProfile.modalConfirmExit.confirm,
   content: (
      <Trans
         i18nKey={appointmentsTranslationKeys.medicalProfile.modalConfirmExit.content}
         components={{ bold: <strong /> }}
      />
   ),
});

const createSelectionAnswer = (questionId: number, code: string): StructuredAnswer => ({
   questionId: questionId,
   selections: [{ code: code }],
});

const collectGatePayloads = (
   viewModel: MedicalProfileTabViewModel,
   row: SectionRowViewModel,
   gateValue: string | null | undefined
): SectionSavePayload[] => {
   const gateChanged = gateValue !== undefined && gateValue !== (row.initialGateCode ?? null);
   if (!gateChanged || !gateValue) {
      return [];
   }
   return [
      {
         templateCode: viewModel.templateCode,
         surveyId: viewModel.surveyId,
         sectionId: row.sectionId,
         sectionTitle: row.title,
         answers: [createSelectionAnswer(row.gateQuestionId, gateValue)],
      },
   ];
};

const createRowVisibilityResolver = (
   viewModel: MedicalProfileTabViewModel,
   row: SectionRowViewModel,
   values: MedicalProfileTabFormValues,
   effectiveGateValue: string | null | undefined
): VisibilityResolver => {
   const getAnswer = (questionId: number): string[] => {
      if (questionId === row.gateQuestionId) {
         return effectiveGateValue ? [effectiveGateValue] : [];
      }
      const value = values[createFormFieldName(viewModel.templateCode, questionId)];
      if (value !== undefined) {
         return valueToCodes(value);
      }
      return answerToCodes(viewModel.initialAnswers.find((answer) => answer.questionId === questionId));
   };
   return createVisibilityResolver(viewModel.template, getAnswer);
};

const collectNodePayloads = (
   viewModel: MedicalProfileTabViewModel,
   row: SectionRowViewModel,
   node: SectionNodeViewModel,
   values: MedicalProfileTabFormValues,
   visibility: VisibilityResolver
): SectionSavePayload[] => {
   // TODO DREGAB-29796: entries of repeatable sections are saved with SectionRecordSaveCommand, not supported yet
   if (node.repeatable || !visibility.isSectionVisible(node.sectionId)) {
      return [];
   }
   const childPayloads = node.childSections.flatMap((childSection) =>
      collectNodePayloads(viewModel, row, childSection, values, visibility)
   );

   const detailValues = node.questions
      .filter((question) => visibility.isQuestionVisible(question.questionId))
      .map((question) => ({
         question: question,
         initialValue: answerToFormValue(question.answerType, question.existingAnswer),
         currentValue: values[createFormFieldName(viewModel.templateCode, question.questionId)],
      }));
   const detailsChanged = detailValues.some(
      (detail) => detail.currentValue !== undefined && !areFormValuesEqual(detail.currentValue, detail.initialValue)
   );
   const answers = detailValues.reduce<StructuredAnswer[]>((detailAnswers, detail) => {
      const value = detail.currentValue === undefined ? detail.initialValue : detail.currentValue;
      const answer = formValueToAnswer(detail.question.questionId, detail.question.answerType, value);
      if (answer) {
         detailAnswers.push(answer);
      }
      return detailAnswers;
   }, []);
   if (!detailsChanged || answers.length === 0) {
      return childPayloads;
   }

   return [
      {
         templateCode: viewModel.templateCode,
         surveyId: viewModel.surveyId,
         sectionId: node.sectionId,
         sectionTitle: node.title ?? row.title,
         answers: answers,
      },
      ...childPayloads,
   ];
};

const collectDetailPayloads = (
   viewModel: MedicalProfileTabViewModel,
   row: SectionRowViewModel,
   values: MedicalProfileTabFormValues,
   gateValue: string | null | undefined
): SectionSavePayload[] => {
   const effectiveGateValue = gateValue === undefined ? row.initialGateCode : gateValue;
   if (effectiveGateValue !== GATE_YES_CODE) {
      return [];
   }
   const visibility = createRowVisibilityResolver(viewModel, row, values, effectiveGateValue);
   return row.details.flatMap((node) => collectNodePayloads(viewModel, row, node, values, visibility));
};

const toGateValue = (value: MedicalProfileAnswerValue | undefined): string | null | undefined => {
   switch (typeof value) {
      case 'string':
         return value;
      case 'undefined':
         return undefined;
      default:
         return null;
   }
};

const collectShowOnVisitPayloads = (
   viewModel: MedicalProfileTabViewModel,
   row: SectionRowViewModel,
   values: MedicalProfileTabFormValues
): SectionSavePayload[] => {
   const currentValue = values[createShowOnVisitFieldName(viewModel.templateCode, row.sectionId)];
   if (typeof currentValue !== 'boolean' || currentValue === row.initialShowOnVisit) {
      return [];
   }
   return [
      applyShowOnVisitToPayload<SectionSavePayload>(
         {
            templateCode: viewModel.templateCode,
            surveyId: viewModel.surveyId,
            sectionId: row.sectionId,
            sectionTitle: row.title,
            answers: [],
         },
         currentValue
      ),
   ];
};

const mergePayloads = (target: SectionSavePayload, source: SectionSavePayload): SectionSavePayload => {
   const merged: SectionSavePayload = { ...target, answers: [...target.answers, ...source.answers] };
   const showOnVisit = source.showOnVisit ?? target.showOnVisit;
   return showOnVisit === undefined ? merged : applyShowOnVisitToPayload(merged, showOnVisit);
};

export const mergeSectionPayloads = (payloads: SectionSavePayload[]): SectionSavePayload[] => {
   const merged = new Map<string, SectionSavePayload>();
   payloads.forEach((payload) => {
      const key = `${payload.templateCode}:${payload.sectionId}`;
      const existing = merged.get(key);
      merged.set(key, existing ? mergePayloads(existing, payload) : payload);
   });
   return Array.from(merged.values());
};

export const collectChangedSections = (
   viewModels: MedicalProfileTabViewModel[],
   values: MedicalProfileTabFormValues
): SectionSavePayload[] =>
   mergeSectionPayloads(
      viewModels.flatMap((viewModel) =>
         viewModel.rows.flatMap((row) => {
            const gateValue = toGateValue(values[createFormFieldName(viewModel.templateCode, row.gateQuestionId)]);
            return [
               ...collectGatePayloads(viewModel, row, gateValue),
               ...collectShowOnVisitPayloads(viewModel, row, values),
               ...collectDetailPayloads(viewModel, row, values, gateValue),
            ];
         })
      )
   );

export const collectChangedSectionsFromForm = (
   viewModels: Map<string, MedicalProfileTabViewModel>,
   formValues: Record<string, unknown>
): SectionSavePayload[] =>
   collectChangedSections(Array.from(viewModels.values()), formValues as MedicalProfileTabFormValues);
